/**
 * Which relationships the ring menu's Create Connection may offer between two
 * entities: the schema's patterns between their two types, in either direction.
 *
 * A pattern `Person -[WORKS_FOR]-> Company` is offered forward when the source
 * is the Person, and reversed when the analyst started from the Company — the
 * edge still runs Person → Company, as the schema says. `POSSIBLE_MATCH` is the
 * system relation entity resolution reviews; every case may hold it. The server
 * applies the same rule (services/manual_graph/rules.py).
 */

import type { Schema } from '@/services/api/schemas'

export const POSSIBLE_MATCH = 'POSSIBLE_MATCH'

export interface RelationOption {
  /** Relationship type as stored, e.g. `WORKS_FOR`. */
  type: string
  /** `reverse`: the edge runs from the picked target to the picked source. */
  direction: 'forward' | 'reverse'
  description?: string
  /** The system relation, offered whatever the schema says. */
  system: boolean
}

const same = (a: string | undefined, b: string | undefined) =>
  Boolean(a && b) && a?.toLowerCase() === b?.toLowerCase()

export function optionKey(option: Pick<RelationOption, 'type' | 'direction'>): string {
  return `${option.direction}:${option.type}`
}

/** The relationships the schema allows from `sourceType` to `targetType`, forward ones first. */
export function relationOptions(
  schema: Schema | null | undefined,
  sourceType: string | undefined,
  targetType: string | undefined
): RelationOption[] {
  const descriptions = new Map(
    (schema?.relationship_types ?? []).map(rt => [rt.label, rt.description?.trim() || undefined])
  )
  const options = new Map<string, RelationOption>()
  const add = (type: string, direction: RelationOption['direction']) => {
    const option = { type, direction, description: descriptions.get(type), system: false }
    if (!options.has(optionKey(option))) options.set(optionKey(option), option)
  }
  const patterns = schema?.patterns ?? []
  for (const p of patterns) {
    if (same(p.source_type, sourceType) && same(p.target_type, targetType)) {
      add(p.relationship_type, 'forward')
    }
  }
  for (const p of patterns) {
    // A self-pattern (Person -[KNOWS]-> Person) is already offered forward.
    if (same(sourceType, targetType)) break
    if (same(p.source_type, targetType) && same(p.target_type, sourceType)) {
      add(p.relationship_type, 'reverse')
    }
  }
  const forwardMatch = options.get(optionKey({ type: POSSIBLE_MATCH, direction: 'forward' }))
  if (!forwardMatch) {
    options.set(optionKey({ type: POSSIBLE_MATCH, direction: 'forward' }), {
      type: POSSIBLE_MATCH,
      direction: 'forward',
      description: descriptions.get(POSSIBLE_MATCH),
      system: true,
    })
  }
  return [...options.values()]
}

/** Whether the schema offers anything beyond the system relation for this pair. */
export function hasSchemaRelation(options: RelationOption[]): boolean {
  return options.some(o => !o.system)
}
